import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import Logo from './Logo';
import LanguageSwitcher from './LanguageSwitcher';
import { useLang } from '../i18n/LanguageContext';

const Navbar = ({ forceSolid = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = forceSolid || scrolled;

  const items = [
    { label: t('nav.identita'), href: '/identita' },
    { label: t('nav.prodotti'), href: '/prodotti' },
    { label: t('nav.attivita'), href: '/attivita' },
    { label: t('nav.servizi'), href: '/servizi' },
    { label: t('nav.contatti'), href: '/contatti' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        solid ? 'bg-white/95 backdrop-blur shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[78px] flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <Logo />
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {items.map((item) => {
            const active = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`text-sm tracking-[0.18em] font-semibold uppercase transition-colors ${
                  solid ? 'text-[#0b2545] hover:text-[#c53114]' : 'text-white hover:text-[#f2b8a6]'
                } ${active ? (solid ? 'text-[#c53114]' : 'text-[#f2b8a6]') : ''}`}
              >
                {item.label}
              </Link>
            );
          })}
          <div className="pl-4 border-l border-[#0b2545]/20">
            <LanguageSwitcher solid={solid} />
          </div>
        </nav>

        <div className="lg:hidden flex items-center gap-4">
          <LanguageSwitcher solid={solid} />
          <button
            aria-label={t('nav.openMenu')}
            className={`p-2 ${solid ? 'text-[#0b2545]' : 'text-white'}`}
            onClick={() => setOpen(true)}
          >
            <Menu size={28} />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-50 transition-opacity ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
        <aside
          className={`absolute top-0 right-0 h-full w-[300px] bg-[#0b2545] text-white p-8 transform transition-transform ${
            open ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <button className="absolute top-5 right-5 text-white" onClick={() => setOpen(false)} aria-label={t('nav.closeMenu')}>
            <X size={26} />
          </button>
          <div className="mt-10">
            <Logo dark />
          </div>
          <ul className="mt-10 space-y-6">
            {items.map((item) => (
              <li key={item.href}>
                <Link to={item.href} className="text-base tracking-[0.18em] uppercase font-semibold hover:text-[#f2b8a6]">
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
          <div className="mt-10 pt-6 border-t border-white/15">
            <LanguageSwitcher />
          </div>
        </aside>
      </div>
    </header>
  );
};

export default Navbar;
